import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@core/api";
import { AgreementSignModal } from "./AgreementSignModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileSignature, faSpinner } from "@fortawesome/pro-light-svg-icons";

type StatusItem = {
  DocumentKey: string;
  Title: string;
  Version: string;
  Required: boolean;
  Completed: boolean;
  Scope: string;
  ExecutionId?: number | null;
  ScopeCustomerId?: number | null;
  ScopeLabel?: string | null;
};

type StatusDto = {
  CustomerId: number;
  SigningCustomerId?: number;
  Items: StatusItem[];
};

type GateScope = "user" | "provider" | "org" | "location";

type Props = {
  /** When set, only these scopes are enforced (e.g. user | provider). */
  scopes?: GateScope[];
  /** Force a customer id; otherwise uses status from user-gates. */
  customerId?: number;
};

/**
 * Blocking gate for outstanding clickwrap / provider agreements after login.
 * Skips Global Admin / Platform Viewer / CSR.
 */
export function AgreementUserGates({ scopes = ["user", "provider"], customerId }: Props) {
  const api = useApi();
  const qc = useQueryClient();
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [activeScope, setActiveScope] = useState<string>("user");
  const [activeCustomerId, setActiveCustomerId] = useState<number | undefined>(customerId);

  const meQ = useQuery({
    queryKey: ["auth-current-agreement-gates"],
    queryFn: () => api["/auth/current"]() as Promise<any>,
  });

  // The API only returns org / location items to Organization Admins / Location Admins,
  // so every gate that enforces user-level agreements also enforces those.
  const effectiveScopes = useMemo(() => {
    const next = new Set<GateScope>(scopes);
    if (next.has("user")) {
      next.add("org");
      next.add("location");
    }
    return Array.from(next);
  }, [scopes]);

  const statusQ = useQuery({
    queryKey: ["agreement-user-gates", customerId ?? "home"],
    queryFn: async () => {
      const res = await api.api.get<StatusDto>("/spg/agreements/user-gates", {
        params: customerId ? { customerId } : undefined,
      });
      return res.data;
    },
    enabled: !!meQ.data?.Id,
    refetchOnWindowFocus: true,
  });

  const pending = useMemo(() => {
    const items = statusQ.data?.Items ?? [];
    return items.filter(
      (i) =>
        i.Required
        && !i.Completed
        && effectiveScopes.includes(i.Scope as GateScope),
    );
  }, [statusQ.data, effectiveScopes]);

  if (meQ.isPending || !meQ.data?.Id) {
    return null;
  }

  if (statusQ.isPending) {
    return null;
  }

  if (pending.length === 0) {
    return null;
  }

  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <>
      <style>{`
        [data-testid="agreement-user-gates"],
        [data-testid="agreement-user-gates"] *:not(svg):not(path) {
          color: #000000 !important;
        }
        [data-testid="agreement-user-gates"] button {
          color: #ffffff !important;
          background: #66AF4E !important;
        }
      `}</style>
      <div
        className="fixed inset-0 z-[10000] flex items-center justify-center bg-[#0C233F]/80 p-4"
        data-testid="agreement-user-gates"
        style={{ color: "#000000" }}>
        <div
          className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl"
          style={{ color: "#000000" }}>
          <div className="mb-3 flex items-center gap-2" style={{ color: "#000000" }}>
            <FontAwesomeIcon icon={faFileSignature} />
            <h2 className="text-lg font-semibold" style={{ color: "#000000" }}>
              Required agreements
            </h2>
          </div>
          <p className="mb-4 text-sm" style={{ color: "#000000" }}>
            Before continuing, please review and accept the following required document
            {pending.length > 1 ? "s" : ""}.
          </p>
          <ul className="mb-4 space-y-2">
            {pending.map((p) => (
              <li
                key={`${p.Scope}-${p.DocumentKey}-${p.ScopeCustomerId ?? "none"}`}
                className="flex items-center justify-between rounded border border-gray-200 px-3 py-2 text-sm"
                style={{ color: "#000000" }}>
                <span className="font-medium" style={{ color: "#000000" }}>
                  {p.Title}
                  {p.ScopeLabel ? ` · ${p.ScopeLabel}` : ""}
                </span>
                <button
                  type="button"
                  className="rounded px-3 py-1.5 text-xs font-semibold"
                  style={{ background: "#66AF4E", color: "#ffffff" }}
                  onClick={() => {
                    setActiveKey(p.DocumentKey);
                    setActiveScope(p.Scope);
                    setActiveCustomerId(
                      p.ScopeCustomerId
                      ?? (p.Scope === "org" ? statusQ.data?.SigningCustomerId : undefined)
                      ?? statusQ.data?.CustomerId
                      ?? customerId,
                    );
                  }}>
                  Review &amp; Sign
                </button>
              </li>
            ))}
          </ul>
          {statusQ.isFetching && (
            <div className="text-xs" style={{ color: "#000000" }}>
              <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
              Checking status…
            </div>
          )}
        </div>
      </div>

      {activeKey && (
        <AgreementSignModal
          open={!!activeKey}
          documentKey={activeKey}
          customerId={activeCustomerId ?? statusQ.data?.CustomerId ?? customerId}
          signerRole={
            activeScope === "provider"
              ? "Provider"
              : activeScope === "org"
                ? "AuthorizedSigner"
                : activeScope === "location"
                  ? "LocationAdmin"
                  : "User"
          }
          relatedEntityKey={
            activeKey === "platform_terms"
              ? (activeScope === "org" ? "org" : "user")
              : activeScope === "location"
                ? "location"
                : null
          }
          extraFieldDefs={
            activeKey === "iou"
              ? [
                  { key: "facilityName", label: "Name of Facility", required: true },
                  { key: "facilityPhone", label: "Facility Phone", required: false },
                ]
              : undefined
          }
          onClose={() => setActiveKey(null)}
          onSigned={() => {
            setActiveKey(null);
            qc.invalidateQueries({ queryKey: ["agreement-user-gates"] });
            qc.invalidateQueries({ queryKey: ["checkout-signature-prefill"] });
          }}
        />
      )}
    </>,
    document.body,
  );
}
